/**
 * 파일명 : com0101m.tsx
 * 설명 : 결재현황화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 * 1.1       2024-03-21   기라성     결재구분 증명서관련인 경우 상세보기 이동 증명서신청으로 가도록 수정
 * 1.2       2024-03-22   김승연     검색조건 '직원명' selectbox로 변경    
 * 1.3       2024-03-27   최희원     증명서관련 결재 화면 이동 변경
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';
import dayjs from 'dayjs';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Com0101mAction } from '../../../redux/slice/com/com0101m_slice';
import { IApprListState, IUserItemState, IApprDcdState, IApprStCdState } from '../../../redux/models/com/com0101m_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Collapse, DatePicker, Steps, ConfigProvider, Table, Select } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';

/************************************************
 * 전역변수 영역
 ************************************************/
const Com0101m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
	/** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const inquiryCnds  = useAppSelector((state) => state.Com0101m.inquiryCnds);
    const apprList     = useAppSelector((state) => state.Com0101m.apprList);
    const userList     = useAppSelector((state) => state.Com0101m.userList);
    const apprDcdList  = useAppSelector((state) => state.Com0101m.apprDcdList);
    const apprStCdList = useAppSelector((state) => state.Com0101m.apprStCdList);
    const pageSize     = useAppSelector((state) => state.Com0101m.pageSize);
    const current      = useAppSelector((state) => state.Com0101m.current);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;
    const { RangePicker } = DatePicker;
    const { Step } = Steps;
    
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        setApprStCdList();
        setApprDcdList();
        setUserList();
        fn_Search();
    }, []);
    

    /**
     * 기능 : setApprStCdList
     * 설명 : 결재상태코드생성
     * param : 
     * return : None
     **/
     const setApprStCdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IApprStCdState[]) => {
                let arrOptions:IApprStCdState[] = [];

                arrOptions.push({
                    label: '전체',
                    value: '',
                });
                datas.forEach((item:IApprStCdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd,
                    });
                });

                dispatch(Com0101mAction.setApprStCdList(arrOptions));
            }

        };
        common.getComCodeList('015', option);
    }
    
    /**
     * 기능 : setApprDcdList
     * 설명 : 결재종류코드생성
     * param : 
     * return : None
     **/
     const setApprDcdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IApprDcdState[]) => {
                let arrOptions:IApprDcdState[] = [];

                arrOptions.push({
                    label: '전체',
                    value: '',
                });
                datas.forEach((item:IApprDcdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd,
                    });
                });

                dispatch(Com0101mAction.setApprDcdList(arrOptions));
            }

        };
        common.getComCodeList('014', option);
    }

    /**
     * 기능 : fn_User
     * 설명 : 사용자 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const setUserList = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Com0101mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        const arrUserOptions = [{label: '전체', value: ''}];
        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);

    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>결재요청번호</div>,
                dataIndex: 'apprNo',
                key: 'apprNo',
                align: 'center',
                width: '7%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>요청일자</div>,
                dataIndex: 'apprReqDt',
                key: 'apprReqDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>결재종류</div>,
                dataIndex: 'apprDnm',
                key: 'apprDnm',
                align: 'center',
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>제목</div>,
                dataIndex: 'title',
                key: 'title',
                align: 'left',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>결재상태</div>,
                dataIndex: 'apprStNm',
                key: 'apprStNm',
                align: 'center',
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상신자</div>,
                dataIndex: 'reqUserNm',
                key: 'reqUserNm',
                align: 'center',
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>진행상태</div>,
                dataIndex: 'apprUserNm',
                key: 'apprUserNm',
                align: 'left',
                width: '30%',
                render: (_:string, recoed:IApprListState) => (
                    <Steps direction='horizontal' current={recoed.apprStCd === '03' ? recoed.currApprOrd + 1 : recoed.apprStCd === '04' || recoed.apprStCd === '08' ? -1 : recoed.currApprOrd} size='small'>
                        {getApprDatas(recoed)}
                    </Steps>
                ),
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>승인일자</div>,
                dataIndex: 'apprDt',
                key: 'apprDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (text:string, record:IApprListState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '5%',
            }];
            return arr;
    }, []);


    const getApprDatas = (record:IApprListState) => {
        // 이권로:반려|윤성수:대기
        const items = record.apprUserNm.split('|');
        const steps = items.map(item => {
            return <Step key={record.apprNo} title={item}/>;
        });
        
        return steps;
    }

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Com0101mAction.setApprList([]));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        const startDate = dateStrings[0].split('-').join('');
        const endDate   = dateStrings[1].split('-').join('');

        dispatch(Com0101mAction.setInquiryCnds({...inquiryCnds, reqStDt: startDate, reqEdDt: endDate}));
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IApprListState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                dispatch(Com0101mAction.initState());
                break;
            }

            case 'btn_showDetail': {
                if (record.apprDcd === '001') {
                    const param = {
                        menuId : 'MUSR2021003',
                        props : {
                            apprNo : record.apprNo
                        }
                    };
                    common.openPage(param);
                } 
                else if (record.apprDcd === '003' || record.apprDcd === '004') {
                    const param = {
                        menuId : 'MCOM20210081',
                        props : {}
                    };
                    common.openPage(param);

                    // common.openPage('증명서신청', 'com0122m', { inquiryCnds, source : { screenId : 'com0101m', title : '결재현황' }, apprNo : record.apprNo, current : state.current });
                }
                else {
                    const param = {
                        menuId : 'MCOM2021012',
                        props : {apprNo : record.apprNo}
                    };
                    common.openPage(param);
                    // common.openPage('결재문서관리', 'com0109m', { inquiryCnds, source : { screenId : 'com0101m', title : '결재현황' }, apprNo : record.apprNo, current : state.current });
                }
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        dispatch(Com0101mAction.setCurrent(1));
        
        /* 변수선언 및 셋팅 영역 */

        // 입력파라미터
        const requestData = {
            ...inquiryCnds,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRCOMR0004',
            success : (responseData:any) => {
                dispatch(Com0101mAction.setApprList(responseData.apprList));
            },
        });
    };

    /**
         * 기능 : 입력 후 Enter key 입력 시 조회
         * 설명 : 입력컴포넌트
         * param : 
         * return : None
         */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };


    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Space>
                                    <Col style={{ paddingLeft: 20 }}>
                                        <Space>
                                            <label>결재요청일자</label>
                                            <ConfigProvider locale={locale}>
                                                <RangePicker
                                                    style={{ width: 305 }}
                                                    id='tx_calendar'
                                                    allowClear={false}
                                                    value={[inquiryCnds.reqStDt ? dayjs(inquiryCnds.reqStDt) : null, inquiryCnds.reqEdDt ? dayjs(inquiryCnds.reqEdDt) : null]}
                                                    onChange={calendar_onChange}
                                                />
                                            </ConfigProvider>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>직원명</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 200,
                                                }}
                                                defaultValue=''
                                                optionFilterProp='label'
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                  
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                  
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={userOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.empNo}
                                                onChange={val => dispatch(Com0101mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                            ></Select>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>결재상태</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 200,
                                                }}
                                                defaultValue=''
                                                optionFilterProp='label'
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                  
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                  
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={apprStCdList}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.apprStCd}
                                                onChange={val => dispatch(Com0101mAction.setInquiryCnds({...inquiryCnds, apprStCd: val}))}
                                            ></Select>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>결재종류</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 200,
                                                }}
                                                defaultValue=''
                                                optionFilterProp='label'
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                  
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                  
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={apprDcdList}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.apprDcd}
                                                onChange={val => dispatch(Com0101mAction.setInquiryCnds({...inquiryCnds, apprDcd: val}))}
                                            ></Select>
                                        </Space>
                                    </Col>
                                </Space>
                                <Col>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={e => onClick(e, null)}
                                        >
                                            초기화
                                    </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={e => onClick(e, null)}
                                        >
                                            조회
                                    </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>결재이력</Text>
                            </Space>
                        </Col>
                    </Row>
                    <Row>
                        <Table dataSource={apprList}
                            key='no'
                            columns={columns()}
                            size='small'
                            style={{width:'100%'}}
                            scroll={{y:550}}
                            pagination={{
                                pageSize: pageSize,
                                current: current,
                                showTotal : () => '총 ' + apprList.length + ' 건',
                                onChange: (page, pageSize) => {
                                    dispatch(Com0101mAction.setCurrent(page));
                                    dispatch(Com0101mAction.setPageSize(pageSize));
                                },
                            }}
                        />
                    </Row>
                </Content>
            </Layout>
        </>
    );
};

export default Com0101m;

